import React from "react";

import { Eyebrow, Title } from "../components/CommonComponents";

import heroImage from "../assets/img/about/vision-mission-hero.webp";
import visionImage from "../assets/img/about/vision.png";
import missionImage from "../assets/img/about/mission.png";
import valuesImage from "../assets/img/about/values.png";

import MetaInfo from "../components/MetaInfo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const visionMissionData = {
  vision:
    "To create a progressive academic environment that inspires knowledge, innovation, responsible leadership and meaningful contribution to society.",

  mission:
    "To nurture competent professionals and responsible citizens through quality education and holistic development.",

  principles: [
    {
      number: "01",
      title: "Knowledge",
      text: "Encouraging intellectual curiosity, academic depth and a lifelong commitment to learning.",
    },
    {
      number: "02",
      title: "Wisdom",
      text: "Helping students connect knowledge with judgement, responsibility and thoughtful decision-making.",
    },
    {
      number: "03",
      title: "Integrity",
      text: "Building a culture rooted in honesty, accountability, ethics and respect.",
    },
    {
      number: "04",
      title: "Excellence",
      text: "Striving for high standards across academics, research, professional development and university life.",
    },
    {
      number: "05",
      title: "Humanity",
      text: "Developing responsible individuals who understand their role within society and the wider world.",
    },
  ],

  missionPillars: [
    {
      title: "Quality Education",
      text: "Strengthening academic foundations through structured, contemporary and meaningful learning experiences.",
    },
    {
      title: "Holistic Development",
      text: "Supporting intellectual, professional, personal and social growth beyond classroom learning.",
    },
    {
      title: "Professional Competence",
      text: "Helping learners develop practical capabilities, confidence and readiness for evolving professional environments.",
    },
    {
      title: "Responsible Citizenship",
      text: "Encouraging ethical awareness, social responsibility and meaningful contribution to communities.",
    },
  ],

  commitments: [
    "Student-centred academic development",
    "Practical and application-oriented learning",
    "Research, inquiry and innovation",
    "Ethical and responsible leadership",
    "Professional and career readiness",
    "Inclusive academic opportunity",
  ],
};

export default function VisionMission() {
  return (
    <>
      <MetaInfo param="" id="about" />

      <Header school="common" />
      <main className="overflow-hidden bg-white">
        <section className="relative isolate min-h-[60vh] overflow-hidden bg-navy text-white lg:min-h-[60vh]">
          <img
            src={heroImage}
            alt="ISBM University campus and students"
            className="absolute inset-0 -z-30 h-full w-full object-cover"
          />

          <div className="absolute inset-0 -z-20 bg-[linear-gradient(90deg,rgba(7,26,47,0.97)_0%,rgba(7,26,47,0.90)_42%,rgba(7,26,47,0.45)_72%,rgba(7,26,47,0.18)_100%)]" />
          <div className="absolute inset-0 -z-10 bg-[linear-gradient(0deg,rgba(7,26,47,0.78)_0%,transparent_48%)]" />

          <div className="mx-auto flex min-h-[60vh] w-[min(1380px,calc(100%-48px))] items-end pb-20 pt-[190px] max-sm:w-[calc(100%-28px)] lg:min-h-[60vh] lg:pb-24">
            <div className="max-w-[880px]">
              <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                <span className="h-px w-12 bg-current" />
                Purpose that guides our progress
              </div>

              <h1 className="mt-7 text-wrap font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(50px,6.5vw,92px)] font-normal leading-[0.96] tracking-[-0.045em]">
                A Clear Purpose <span className="text-[#D5AD57]">Guides </span>
                Every Step Forward
              </h1>

              <p className="mt-8 max-w-[720px] text-[17px] leading-8 text-white/70">
                Our vision and mission express the principles that guide ISBM
                University as an academic community - shaping how we educate,
                develop and prepare students for meaningful futures.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#vision"
                  className="inline-flex min-h-[56px] items-center gap-8 bg-gold px-7 text-sm font-bold uppercase tracking-[0.1em] text-navy transition hover:bg-white"
                >
                  Explore Our Vision
                  <span>↓</span>
                </a>

                <a
                  href="#mission"
                  className="inline-flex min-h-[56px] items-center gap-8 border border-white/30 px-7 text-sm font-bold uppercase tracking-[0.1em] text-white transition hover:border-gold hover:text-gold"
                >
                  Our Mission
                  <span>↗</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-20 sm:py-24 lg:py-28">
          <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
            <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24">
              <div>
                <Eyebrow>Our Purpose</Eyebrow>
                <div className="mt-12 hidden lg:block">
                  <span className="font-serif text-[132px] leading-none text-burgundy/[0.07]">
                    WHY
                  </span>
                </div>
              </div>

              <div>
                <Title
                  before="Education With"
                  after="Beyond the Classroom"
                  highlight="Purpose"
                  position="middle"
                  theme="burgundy"
                />

                <div className="mt-8 grid gap-8 md:grid-cols-2">
                  <p className="text-[17px] leading-8 text-ink/70">
                    ISBM University&apos;s vision and mission provide a
                    foundation for its academic direction, student development
                    and institutional culture.
                  </p>

                  <p className="text-[15px] leading-8 text-ink/55">
                    Together, they connect academic learning with professional
                    competence, responsible citizenship and holistic development
                    - creating a university experience that looks beyond
                    qualification alone.
                  </p>
                </div>

                <div className="mt-10 h-px w-full bg-navy/10" />
              </div>
            </div>
          </div>
        </section>

        <section
          id="vision"
          className="relative overflow-hidden bg-[#EEF4F8] py-20 sm:py-24 lg:py-28"
        >
          <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
            <div className="grid gap-12 lg:grid-cols-[1.05fr_.95fr] lg:items-center lg:gap-20">
              <div>
                <Eyebrow>Our Vision</Eyebrow>

                <Title
                  before="Looking Beyond"
                  after="What Education Can Become"
                  highlight="Today"
                  position="middle"
                  theme="burgundy"
                />

                <div className="mt-10 border-l-[3px] border-gold pl-7 sm:pl-9">
                  <p className="font-serif text-[clamp(26px,2.7vw,38px)] font-normal leading-[1.45] text-navy">
                    “{visionMissionData.vision}”
                  </p>
                </div>

                <p className="mt-8 max-w-[670px] text-[15px] leading-8 text-ink/60">
                  Our vision reflects a commitment to creating an academic
                  environment where learning can lead to individual growth,
                  professional capability and meaningful contribution.
                </p>
              </div>

              <div className="relative min-h-[520px] sm:min-h-[620px]">
                <figure className="absolute inset-0 overflow-hidden">
                  <img
                    src={visionImage}
                    alt="Students walking across the ISBM University campus"
                    className="h-full w-full object-cover"
                  />
                </figure>

                <div className="absolute bottom-0 left-0 bg-navy px-7 py-7 text-white sm:px-9">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-gold">
                    Our Direction
                  </span>

                  <p className="mt-3 max-w-[310px] font-serif text-[24px] leading-[1.35]">
                    Creating possibilities through education.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="mission"
          className="relative overflow-hidden bg-burgundy py-20 text-white sm:py-24 lg:py-28"
        >
          <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
            <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
              <div className="lg:sticky lg:top-[170px] lg:self-start">
                <Eyebrow dark>Our Mission</Eyebrow>

                <Title
                  before="Turning Purpose"
                  after="Meaningful Action"
                  highlight="Into"
                  position="middle"
                  dark
                />

                <div className="mt-10 border-l-[3px] border-gold pl-7 sm:pl-9">
                  <p className="font-serif text-[clamp(26px,2.7vw,38px)] font-normal leading-[1.45] text-white">
                    “{visionMissionData.mission}”
                  </p>
                </div>

                <div className="mt-9 h-px w-20 bg-gold" />
              </div>

              <div className="border-t border-white/15">
                {visionMissionData.missionPillars.map((item, index) => (
                  <article
                    key={item.title}
                    className="grid gap-5 border-b border-white/15 py-8 sm:grid-cols-[70px_.8fr_1.2fr] sm:items-start lg:py-10"
                  >
                    <span className="font-serif text-[30px] text-gold">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <h3 className="font-serif text-[27px] font-normal leading-[1.2] text-white">
                      {item.title}
                    </h3>

                    <p className="max-w-[500px] text-sm leading-7 text-white/55">
                      {item.text}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-ivory py-20 sm:py-24 lg:py-28">
          <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
            <Eyebrow>Values That Shape Us</Eyebrow>

            <Title
              before="Principles That"
              after="University Community"
              highlight="Guide Our"
              position="middle"
              theme="burgundy"
            />

            <div className="mt-12 grid border-l border-t border-navy/10 sm:grid-cols-2 lg:grid-cols-5">
              {visionMissionData.principles.map((item) => (
                <article
                  key={item.number}
                  className="min-h-[300px] border-b border-r border-navy/10 bg-white/40 p-7 transition-colors hover:bg-white lg:p-8"
                >
                  <span className="font-serif text-[32px] text-gold-dark">
                    {item.number}
                  </span>

                  <div className="mt-16">
                    <h3 className="font-serif text-[28px] font-normal text-navy">
                      {item.title}
                    </h3>

                    <p className="mt-4 text-sm leading-7 text-ink/55">
                      {item.text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 sm:py-24 lg:py-28">
          <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
            <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-24">
              <div className="relative min-h-[560px] overflow-hidden">
                <img
                  src={missionImage}
                  alt="Academic learning at ISBM University"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>

              <div>
                <Eyebrow>Mission in Practice</Eyebrow>

                <Title
                  before="From Words"
                  after="The Student Experience"
                  highlight="To"
                  position="middle"
                  theme="burgundy"
                />

                <div className="mt-9 grid sm:grid-cols-2">
                  {visionMissionData.commitments.map((item, index) => (
                    <div
                      key={item}
                      className="flex min-h-[96px] items-center gap-5 border-b border-navy/10 py-5 sm:border-r sm:px-5"
                    >
                      <span className="font-serif text-[22px] text-gold-dark">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <strong className="text-sm font-semibold leading-6 text-navy">
                        {item}
                      </strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative min-h-[650px] overflow-hidden bg-navy text-white">
          <img
            src={valuesImage}
            alt="ISBM University students in an academic environment"
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,26,47,0.96)_0%,rgba(7,26,47,0.82)_48%,rgba(7,26,47,0.28)_100%)]" />

          <div className="relative mx-auto flex min-h-[650px] w-[min(1380px,calc(100%-48px))] items-center py-20 max-sm:w-[calc(100%-28px)]">
            <div className="ms-auto max-w-[700px]">
              <div className="flex items-center gap-3 text-sm font-extrabold uppercase tracking-[0.14em] text-[#D5AD57] justify-end">
                <span className="h-px w-10 bg-current" />A Shared Academic
                Culture
              </div>

              <h2 class="mt-6 max-w-[850px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.8vw,58px)] font-normal leading-[1.05] tracking-[-0.035em] text-white text-end">
                Knowledge Builds{" "}
                <em class="font-normal text-[#8B1E3F]">Character{" "}</em> Gives It
                Meaning
              </h2>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 justify-end">
                {[
                  "Knowledge",
                  "Wisdom",
                  "Integrity",
                  "Excellence",
                  "Humanity",
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-3 text-sm font-semibold text-white/80"
                  >
                    <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
